/*

  # Accessible modal

  ## Attribute API

  - data-modal-id is required on modals and open buttons.
  - To control the styles of the modal element states, use
    the data-class-modal-open and data-class-modal-closed attributes.

  ## Co-controller patterns

  - modal-media (modalMedia controller)
    Stops iframes, video and audio elements from playing when the
    modal is closed.
  - modal-top-level (modalTopLevel controller)
    Moves the modal to the document body
  - modal-bg-image (modalBgImage controller)
    Refreshes bgImage controllers inside the modal once it is opened
  - modal-pointer-close (modalPointerClose controller)
    Closes when clicking outside in the DOM tree
  - modal-pointer-shader-close (modalPointerShaderClose controller)
    Closes when clicking the controlled element but not its
    descendants
  - modal-key-close (modalKeyClose controller)
    Closes when pressing the escape key
  - modal-auto-focus (modalAutoFocus controller)
    Automatically focuses a control element
  - modal-auto-open (modalAutoOpen controller)
    Automatically opens the modal
  - To lock the document behind the modal, add either:
    - modal-position-lock (modalPositionLock controller)
      Prevents scrolling using viewport scroll position
    - modal-overflow-lock (modalOverflowLock controller)
      Prevents scrolling using an ancestor element's
      overflow property

  ## Accessibility behaviors and instructions

  - id, aria-controls, aria-labelledby, role attributes are
    automatically generated by the script when not present and do
    not need to be added to the markup.
  - Controls aria-expanded and aria-live attributes upon open/close
  - Use visibility or display to make the modal invisible to
    screen readers when closed

  ## Other notes

  - You can have as many open and close buttons as you need
  - To make the entire modal background a close button, stretch
    an absolutely-positioned close button contents and then style
    it to have no opacity and sit behind the modal's contents. Use
    cursor: auto to make the pointer not appear as though it is
    a button.

  ## Modal with close button:

  <div class="d-none"
    data-hook="modal"
    data-modal-id="search"
    data-class-modal-open="d-block"
    data-class-modal-closed="d-none">
    <button type="button" data-hook="modal.closeButton">
    </button>
  </div>

  ## Modal open button:

  <button type="button" data-hook="modal.openButton" data-modal-id="search">
  </button>

  */

import {
  assignController, click, selectAlso, selectAll, setClassState, unsetClassState,
} from '../api/element-manipulation'

const existingIds = []

assignController('modal', (element) => {
  element.setAttribute('role', 'region')
  if (!element.id) {
    element.id = `modal-area-${element.dataset.modalId}`
  }

  if (existingIds.includes(element.dataset.modalId)) {
    element.parentNode.removeChild(element)
    return
  }
  existingIds.push(element.dataset.modalId)


  const opened = []
  const closed = []

  const styleElements = selectAlso(element, '[data-class-modal-open]')

  const close = (event) => {
    if ((!event.bypassModalOpenAttribute) && (element.dataset.modalOpen !== '1')) {
      return
    }
    element.dataset.modalOpen = '0'
    if (!event.omitModalClosedEvents) {
      closed.forEach(listener => listener({ sender: controller }))
    }
    styleElements.forEach((styleElement) => {
      unsetClassState(styleElement, styleElement.dataset.classModalOpen)
      setClassState(styleElement, styleElement.dataset.classModalClosed)
    })
    element.setAttribute('aria-expanded', 'false')
    element.setAttribute('aria-live', 'off')
  }
  const open = (event) => {
    element.dataset.modalOpen = '1'
    styleElements.forEach((styleElement) => {
      unsetClassState(styleElement, styleElement.dataset.classModalClosed)
      setClassState(styleElement, styleElement.dataset.classModalOpen)
    })
    element.setAttribute('aria-expanded', 'true')
    element.setAttribute('aria-live', 'polite')
    opened.forEach(listener => listener({ sender: controller }))
  }
  close({ omitModalClosedEvents: true, bypassModalOpenAttribute: true })
  const closeButtons = selectAll(element, '[data-hook~="modal.closeButton"]')
  const openButtons = selectAll(document, `[data-hook~="modal.openButton"][data-modal-id="${element.dataset.modalId}"]`)
  closeButtons.forEach((button, index) => {
    click(button, close)
    if (!button.id) {
      button.id = `modal-close-${element.dataset.modalId}-${index}`
    }
    element.setAttribute('aria-controls', element.id)
  })
  openButtons.forEach((button, index) => {
    click(button, open)
    if (!button.id) {
      button.id = `modal-open-${element.dataset.modalId}-${index}`
    }
    if (index === 0) {
      element.setAttribute('aria-labelledby', button.id)
    }
    element.setAttribute('aria-controls', element.id)
  })
  const controller = {
    element,
    close,
    open,
    closeButtons,
    openButtons,
    opened,
    closed,
    existingIds,
  }
  return controller
})
